import { WarningButton } from "@components/buttons/warning";
import { SHBox, SHStack, SHTypography } from "@components/design-systems";
import { EyeIcon } from "@components/svgs/eye";
import { WarningSVG } from "@components/svgs/warning";
import { FeeSubProductDTO } from "@models/platform-analysis/entities/steps/fee";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import { FeeHeatmapMode } from "@models/platform-analysis/enums/fee/heatmapMode";
import { Collapse, useTheme } from "@mui/material";
import { FeeHeatmapProps } from "@pages/platform-analysis/_id/steps/fee/analysis/components/heatmap";
import { VerticalDashLineV2 } from "@pages/platform-analysis/_id/steps/fee/analysis/components/heatmap-v2/components/vertical-dash-line";
import { LabelLineDetail } from "@pages/platform-analysis/_id/steps/fee/analysis/components/heatmap/components/label-line-detail";
import { LabelLineProjection } from "@pages/platform-analysis/_id/steps/fee/analysis/components/heatmap/components/label-line-projection";
import { FeeAnalysisLabelConstants } from "@pages/platform-analysis/_id/steps/fee/analysis/constants";
import { PDFFeeAnalysisConfig } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/fee-analysis/configs";
import {
  displayCurrencyNumber,
  getFeeDetailMinMaxExcludePerformanceFees,
  getFeeMinMaxExcludePerformanceFees,
} from "@pages/platform-analysis/util";
import {
  formatPercentage,
  getHSLLightness,
  hslToHex,
  thousandSeparator,
} from "@utils";
import { defaultTo, isNull } from "lodash";
import { useMemo } from "react";
import { TransitionGroup } from "react-transition-group";
import {
  AVG_LIGHTNESS,
  DEFAULT_HUE,
  DEFAULT_SATURATION,
  MAX_LIGHTNESS,
  MIN_LIGHTNESS,
} from "../heatmap/config";
import { FeeSubProductCard } from "../sub-product-card";

interface FeeHeatMapV2Props extends FeeHeatmapProps {
  isExcludePerformanceFees?: boolean;
  hiddenIdIndex?: boolean[];
}

export const FeeHeatMapV2 = ({
  subProducts,
  totalPortfolioValue,
  mode = FeesDisplayStyle.Dollar,
  heatmapMode = FeeHeatmapMode.Projection,
  hiddenIds = [],
  color = {
    hue: DEFAULT_HUE,
    saturation: DEFAULT_SATURATION,
  },
  minLightness = MIN_LIGHTNESS,
  maxLightness = MAX_LIGHTNESS,
  avgLightness = AVG_LIGHTNESS,
  titleComp,
  bottomLegend,
  captions = {
    dollar: "Total fees",
    percentage: "of total portfolio value",
  },
  showWarningCaptions,
  onChangeHiddenIds,
  isCompleted,
  isSummaryStep,
  isExcludePerformanceFees,
  hiddenIdIndex = [],
  ...others
}: FeeHeatMapV2Props) => {
  const { palette } = useTheme();
  const isDollarMode = mode === FeesDisplayStyle.Dollar;
  const isProjectionMode = heatmapMode === FeeHeatmapMode.Projection;

  const subProductsFiltered = useMemo(
    () =>
      subProducts.filter((subProduct) => !hiddenIds.includes(subProduct.id)),
    [subProducts, hiddenIds],
  );

  const counterHiddenSubProduct = useMemo(() => {
    return hiddenIdIndex.filter((item) => item).length;
  }, [hiddenIdIndex]);

  const showWarning = useMemo(() => {
    return subProductsFiltered?.some(
      (subProduct: FeeSubProductDTO) => subProduct.warning,
    );
  }, [subProductsFiltered]);

  const renderDetails = () => {
    if (!subProductsFiltered[0]?.feeDetails) return null;
    const firstProductFeeDetails = subProductsFiltered[0]?.feeDetails ?? [];
    const arrayMinMax = getFeeDetailMinMaxExcludePerformanceFees({
      subProducts: subProductsFiltered,
      isDollarMode: isDollarMode,
      isExcludePerformanceFees,
    });
    const hasTooManyFeeColumns =
      firstProductFeeDetails.length >
      PDFFeeAnalysisConfig.MaxFeeDetailsColumnToDisplayLogo;

    /* Detail Mode */

    return (
      <SHStack flexGrow={1} spacing={"25px"} width={"1px"}>
        <SHStack spacing={"1px"}>
          <LabelLineDetail
            firstProductFeeDetails={firstProductFeeDetails}
            isFamilyLevel
            isExcludePerformanceFees={isExcludePerformanceFees}
          />
          <VerticalDashLineV2
            numOfItems={firstProductFeeDetails.length}
            firstProductFeeDetails={firstProductFeeDetails}
            isFamilyLevel
          />

          <SHStack direction={"row"} spacing={"1px"}>
            <SHStack justifyContent={"space-between"}>
              {Array.from(Array(subProductsFiltered.length + 1).keys()).map(
                (_, index) => (
                  <SHStack
                    key={index}
                    width={"5px"}
                    height={"1px"}
                    sx={{
                      background: palette.text.primary,
                      transform:
                        index === 0
                          ? "translateY(1.5px)"
                          : index === subProductsFiltered.length
                          ? "translateY(-1.5px)"
                          : undefined,
                    }}
                  />
                ),
              )}
            </SHStack>
            <TransitionGroup
              style={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                width: "1px",
              }}
            >
              {subProductsFiltered.map((subProduct) => (
                <Collapse key={subProduct.id}>
                  <SHStack direction={"row"} flex={1} display={"relative"}>
                    {subProduct.feeDetails.map((feeDetail, index) => {
                      const { min, max } = arrayMinMax[index] ?? {
                        min: 0,
                        max: 0,
                      };

                      const dollarValueToRender = !isExcludePerformanceFees
                        ? feeDetail.dollar
                        : defaultTo(feeDetail.dollarExcPerf, feeDetail.dollar);
                      const percentageValueToRender = !isExcludePerformanceFees
                        ? feeDetail.percentage
                        : defaultTo(
                            feeDetail.percentageExcPerf,
                            feeDetail.percentage,
                          );

                      const lightness = getHSLLightness(
                        min,
                        max,
                        isDollarMode
                          ? dollarValueToRender || 0
                          : percentageValueToRender || 0,
                        minLightness,
                        maxLightness,
                      );
                      const isDarkCell = lightness < avgLightness;

                      const displayData =
                        isNull(percentageValueToRender) ||
                        isNull(dollarValueToRender)
                          ? "No data"
                          : isDollarMode
                          ? `$${thousandSeparator(
                              +dollarValueToRender.toFixed(0),
                            )}`
                          : `${formatPercentage(percentageValueToRender)}%`;

                      return (
                        <SHStack
                          key={index}
                          flex={1}
                          justifyContent={"center"}
                          alignItems={"center"}
                          sx={{
                            height: "50px",
                            maxWidth:
                              hasTooManyFeeColumns &&
                              feeDetail.name ===
                                FeeAnalysisLabelConstants.PYSRefund
                                ? "50px"
                                : "100%",
                            backgroundColor: hslToHex(
                              color.hue,
                              color.saturation,
                              lightness,
                            ),
                            transition: "all ease-in-out 0.2s",
                            "&:hover": {
                              filter: "brightness(80%)",
                            },
                            my: "1.5px",
                            mr: index === 0 ? "15px" : "1.5px",
                            ml:
                              index === subProduct.feeDetails.length - 1
                                ? "25px"
                                : "1.5px",
                          }}
                        >
                          <SHTypography
                            variant="body2"
                            color={
                              isDarkCell
                                ? palette.common.white
                                : palette.text.darker
                            }
                          >
                            {displayData}
                          </SHTypography>
                        </SHStack>
                      );
                    })}
                  </SHStack>
                </Collapse>
              ))}
            </TransitionGroup>
          </SHStack>

          <VerticalDashLineV2
            numOfItems={firstProductFeeDetails.length}
            firstProductFeeDetails={firstProductFeeDetails}
            isFamilyLevel
          />
          <LabelLineDetail
            firstProductFeeDetails={firstProductFeeDetails}
            isFamilyLevel
            isExcludePerformanceFees={isExcludePerformanceFees}
          />
        </SHStack>
        {/*   End */}
      </SHStack>
    );
  };

  const renderProjection = () => {
    const firstProductFees = subProductsFiltered[0]?.fees ?? [];
    const { min, max } = getFeeMinMaxExcludePerformanceFees({
      subProducts: subProductsFiltered,
      isDollarMode: isDollarMode,
      isExcludePerformanceFees,
    });

    return (
      <SHStack flexGrow={1} spacing={"25px"} width={"1px"}>
        <SHStack spacing={"1px"}>
          <LabelLineProjection firstProductFees={firstProductFees} />
          <VerticalDashLineV2
            numOfItems={firstProductFees.length}
            type={FeeHeatmapMode.Projection}
          />
          <SHStack direction={"row"} spacing={"1px"}>
            <SHStack justifyContent={"space-between"}>
              {Array.from(Array(subProductsFiltered.length + 1).keys()).map(
                (_, index) => (
                  <SHStack
                    key={index}
                    width={"5px"}
                    height={"1px"}
                    sx={{
                      background: palette.text.primary,
                      transform:
                        index === 0
                          ? "translateY(1.5px)"
                          : index === subProductsFiltered.length
                          ? "translateY(-1.5px)"
                          : undefined,
                    }}
                  />
                ),
              )}
            </SHStack>
            <TransitionGroup
              style={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                width: "1px",
              }}
            >
              {subProductsFiltered.map((subProduct) => (
                <Collapse key={subProduct.id}>
                  <SHStack direction={"row"} flex={1}>
                    {subProduct.fees.map((fee, index) => {
                      const displayDollarValue = isExcludePerformanceFees
                        ? defaultTo(
                            fee.totalCostExcPerfForDisplay,
                            fee.totalCostForDisplay,
                          )
                        : fee.totalCostForDisplay;
                      const displayPercentageValue = isExcludePerformanceFees
                        ? defaultTo(
                            fee.totalCostExcPerfForDisplayPercentage,
                            fee.totalCostForDisplayPercentage,
                          )
                        : fee.totalCostForDisplayPercentage;

                      const lightness = getHSLLightness(
                        min,
                        max,
                        isDollarMode
                          ? displayDollarValue
                          : displayPercentageValue,
                        minLightness,
                        maxLightness,
                      );
                      const isDarkCell = lightness < avgLightness;

                      const displayData = isDollarMode
                        ? `$${thousandSeparator(
                            +displayDollarValue.toFixed(0),
                          )}`
                        : `${formatPercentage(displayPercentageValue)}%`;

                      return (
                        <SHStack
                          key={index}
                          flex={1}
                          justifyContent={"center"}
                          alignItems={"center"}
                          sx={{
                            height: "50px",
                            backgroundColor: hslToHex(
                              color.hue,
                              color.saturation,
                              lightness,
                            ),
                            transition: "all ease-in-out 0.2s",
                            "&:hover": {
                              filter: "brightness(80%)",
                            },
                            m: "1.5px",
                          }}
                        >
                          <SHTypography
                            variant="body2"
                            color={
                              isDarkCell
                                ? palette.common.white
                                : palette.text.darker
                            }
                          >
                            {displayData}
                          </SHTypography>
                        </SHStack>
                      );
                    })}
                  </SHStack>
                </Collapse>
              ))}
            </TransitionGroup>
          </SHStack>
          <VerticalDashLineV2
            numOfItems={firstProductFees.length}
            type={FeeHeatmapMode.Projection}
          />
          <LabelLineProjection firstProductFees={firstProductFees} />
        </SHStack>
        {/*   End */}
      </SHStack>
    );
  };

  const renderFeeTotalBalanceText = () => {
    return (
      <SHStack
        direction={"row"}
        gap={1}
        alignItems={"center"}
        sx={{
          marginLeft: "0px",
        }}
      >
        {isProjectionMode ? (
          <>
            <SHTypography variant="subtitle2">
              {isDollarMode ? " $" : "%"}
            </SHTypography>
            <SHTypography colorVariant="third" variant="subtitle2">
              {isDollarMode ? captions.dollar : captions.percentage}
            </SHTypography>
          </>
        ) : (
          <>
            <SHTypography variant="subtitle2">
              {isDollarMode ? " $ " : "%"}
            </SHTypography>
            <SHTypography colorVariant="third" variant="subtitle2">
              {`Fees for a total balance of ${displayCurrencyNumber(
                totalPortfolioValue ?? 0,
              )}`}
            </SHTypography>
          </>
        )}
      </SHStack>
    );
  };

  const renderBottomChart = () => {
    return (
      <SHStack direction={"row"} marginTop={"20px"}>
        <SHStack
          textAlign={"left"}
          width={"200px"}
          paddingLeft={isSummaryStep ? 0 : "28px"}
        >
          <SHTypography color={palette.primary.main} variant="subtitle2">
            {`${counterHiddenSubProduct} ${
              counterHiddenSubProduct === 1 ? "product" : "products"
            } hidden`}
          </SHTypography>
        </SHStack>
        <SHStack
          flex={1}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={showWarningCaptions ? "flex-start" : "center"}
        >
          <SHStack>
            {showWarning && (
              <SHStack
                direction={"row"}
                spacing={1}
                alignItems={"center"}
                ml={"-3px"}
              >
                <WarningSVG
                  backgroundColor={palette.warning.main}
                  iconColor={palette.common.white}
                />
                <SHTypography colorVariant="third" variant="body2">
                  Product is missing some investment options
                </SHTypography>
              </SHStack>
            )}
          </SHStack>

          {bottomLegend}
        </SHStack>
      </SHStack>
    );
  };

  const hasData =
    subProductsFiltered.length &&
    (isProjectionMode
      ? subProductsFiltered[0].fees?.length
      : subProductsFiltered[0].feeDetails?.length);

  return (
    <SHStack
      spacing={"25px"}
      padding={"28px"}
      paddingLeft={0}
      sx={{
        background: palette.common.white,
        border: `1px solid ${palette.secondary[100]}`,
      }}
      {...others}
    >
      <SHStack paddingLeft={isSummaryStep ? 0 : "28px"}>
        {titleComp}
        {renderFeeTotalBalanceText()}
      </SHStack>
      {hasData ? (
        <SHStack>
          <SHStack
            direction={"row"}
            alignItems={"stretch"}
            width={"100%"}
            height={"100%"}
          >
            <SHStack sx={{ alignItems: "center", justifyContent: "center" }}>
              <TransitionGroup
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginLeft: isSummaryStep ? 0 : "25px",
                }}
              >
                {subProductsFiltered.map((subProduct) => (
                  <Collapse key={subProduct.id}>
                    <SHStack
                      className={`FeeChartSection-heatmap-sub-product-${subProduct.productId}`}
                      direction="row"
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      width={"190px"}
                      height={"53px"}
                      onClick={() => {
                        if (isCompleted && isSummaryStep) return;
                        const isActive = !hiddenIds.includes(subProduct.id);
                        if (isActive) {
                          onChangeHiddenIds?.([...hiddenIds, subProduct.id]);
                        } else {
                          onChangeHiddenIds?.(
                            hiddenIds.filter((id) => id !== subProduct.id),
                          );
                        }
                      }}
                      sx={{
                        cursor:
                          isCompleted && isSummaryStep ? "default" : "pointer",
                      }}
                    >
                      <FeeSubProductCard
                        isActive
                        subProduct={subProduct}
                        sx={{
                          "& .FeeSubProductCard-productName": {
                            fontWeight: "bold !important",
                          },
                          "& .FeeSubProductCard-subProductName": {
                            fontWeight: "normal !important",
                          },
                        }}
                      />
                      <SHStack>
                        <SHBox
                          sx={{
                            padding: 0,
                            width: "24px",
                            alignItems: "flex-end",
                            justifyContent: "center",
                            marginBottom: "4px",
                            display: "flex",
                            "&:hover":
                              isCompleted && isSummaryStep
                                ? undefined
                                : {
                                    "& .sh-image-svg": {
                                      "& .sh-eye-svg-g": {
                                        "& .sh-icon-svg-eye": {
                                          fill: palette.grey[500],
                                        },
                                      },
                                    },
                                  },
                          }}
                        >
                          <EyeIcon isActive={false} />
                        </SHBox>
                        {subProduct.warning && (
                          <SHStack>
                            <WarningButton
                              title={"Missing investment option"}
                              content={
                                <ul
                                  style={{ margin: 0, padding: "5px 0 0 15px" }}
                                >
                                  {subProduct.warning.map((content, index) => (
                                    <li
                                      key={index}
                                      style={{ padding: "4px 0" }}
                                    >
                                      {content}
                                    </li>
                                  ))}
                                </ul>
                              }
                              customSVG={
                                <SHStack mb={"-3px"}>
                                  <WarningSVG
                                    backgroundColor={palette.warning.main}
                                    iconColor={palette.common.white}
                                  />
                                </SHStack>
                              }
                            />
                          </SHStack>
                        )}
                      </SHStack>
                    </SHStack>
                  </Collapse>
                ))}
              </TransitionGroup>
            </SHStack>
            {isProjectionMode ? renderProjection() : renderDetails()}
          </SHStack>
          {renderBottomChart()}
        </SHStack>
      ) : (
        <SHStack paddingLeft={"28px"}>
          <SHTypography variant="subtitle1" colorVariant="disabled">
            No data
          </SHTypography>
          <SHTypography variant="body2" colorVariant="disabled">
            No products match the criteria in your filter
          </SHTypography>
          <SHTypography
            color={palette.primary.main}
            variant="subtitle2"
            sx={{
              marginTop: "25px",
            }}
          >
            {`${counterHiddenSubProduct} ${
              counterHiddenSubProduct === 1 ? "product" : "products"
            } hidden`}
          </SHTypography>
        </SHStack>
      )}
    </SHStack>
  );
};
